/*
  Landing-page data and motion variants, lifted out of App.tsx (1,638 lines, one
  1,293-line component). Pure values only — no JSX, so this stays a .ts file.

  The demo content below is *sample* content — the topics and messages stand in
  for a real feed, not for UI copy — so it lives here rather than in the locale
  files, one set per locale. Mongolian is the default locale and the primary
  visitor reads it, so the Mongolian set is written as a campus week of its own,
  not a translation (and is machine-drafted: see
  .scratch/landing-redesign/unapproved-copy.md). Every actual label around it
  goes through i18n.
*/
import comedyLineup from "@/assets/loops/comedy-lineup.svg";
import comedyNight from "@/assets/loops/comedy-night.svg";

// ── Section tones ─────────────────────────────────────────────────────────────
/*
  The tone a room section paints with, applied as Tailwind classes rather than
  the six raw color-mix() strings in `style` attributes this started as — that
  is how the landing page ended up with a colour vocabulary no lint rule could
  see.

  ── Why there is one tone and not three ─────────────────────────────────────
  There used to be three: the accent for Loops, `identity-5` for Strings and
  `identity-6` for Knots. `identity-*` is the **data-encoding** ramp —
  `getIdentityColor(id)` hands those six hues to avatars so two accounts can be
  told apart, and DESIGN.md §5 is why they never desaturate.

  Borrowing them for section chrome inverted the hierarchy in exactly the theme
  that cares most. Graphite is deliberately monochrome, so its accent is white
  and Loops — which holds the product accent — went grey there, while Knots
  stayed fully blue and Strings fully amber: the two borrowed hues outranked
  the product's own. Paper had the mirror of the same problem, a light theme
  carrying two saturated mid-lightness hues it never asked for.

  So the tone is derived from the theme's accent seed, which is what DESIGN.md
  rule 5 ("one accent hue") asked for all along. A monochrome theme is now
  monochrome, a light theme stays light, and all seven themes get a correct
  value with no per-theme branching and nothing new in globals.css. The rooms
  are told apart the way the rooms comparison tells them apart — by glyph, by
  position and by what the demo beside them actually shows — not by hue.

  There used to be three exported names — L_TONE, S_TONE and K_TONE — left as
  aliases of this one while the sections were being rebuilt. They are gone.
  Three identifiers for one value reads as three decisions a theme could make
  differently, and no theme can: they resolved to the same object, so every
  call site was already painting the same colour while appearing to choose. If
  a future theme does want to split the rooms apart again, splitting one
  constant into three is the smaller change of the two.
*/
export interface SectionTone {
  /** Chip / card ground: tint plus its matching border. */
  box: string;
  /** Section colour as text. */
  text: string;
  /** Section colour as an SVG stroke. */
  stroke: string;
  /** Section colour as a solid fill, for dots and rules. */
  fill: string;
}

export const ROOM_TONE: SectionTone = {
  box: "bg-accent/10 border border-accent/22",
  text: "text-accent-text",
  stroke: "stroke-accent-text",
  fill: "bg-accent-text",
};


// ── Animation variants ─────────────────────────────────────────────────────────
export const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};

/* Hard-coded at four call sites before this. See AppStoreLink.tsx. */
export const APP_STORE_ID = "6745579771";
export const APP_STORE_URL = `https://apps.apple.com/us/app/astring-chaos/id${APP_STORE_ID}`;

/* The Android app's public Google Play listing (the package the intent:// links name). */
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.astring.app";

// ── Content data ───────────────────────────────────────────────────────────────

/**
 * One Loop in the landing feed, with the conversation behind it.
 *
 * This mirrors what `/y/loops` actually renders — a `LoopCard` in the feed, and
 * the room's chat when you open one (ADR 0009 §6: opening a Loop opens its
 * chat, there is no third surface between them). The demo exists to show that
 * relationship, so the two halves have to stay consistent with each other: a
 * card's live/archived state is the state its chat header shows, and an archived Loop's
 * chat has no composer. A live Loop shows that it is live, never how many are
 * in it: the demo carries no counts (PRODUCT.md, Claims Policy).
 */
export interface LoopExample {
  id: string;
  topic: string;
  /**
   * Identity mode, fixed at creation and immutable (CONTEXT.md). In an
   * anonymous Loop every sender — the creator included — posts under a per-Loop
   * pseudonym, which is why `author` and every `sender` below are pool names.
   */
  anonymous: boolean;
  author: string;
  /** Archived: read-only, no composer. Every other demo Loop is live. */
  archived?: boolean;
  /**
   * Header images, up to six on a real Loop (`RoomMeta.images`). Imported asset
   * URLs here rather than media references: there is no API on this page, and
   * the resolution rule in `packages/services/media-ref.ts` already passes an
   * `http(s)://` value through untouched, so this is the shape the real
   * component would hand to an `<img>` anyway.
   *
   * Worth knowing when editing the copy around this: header images are gated on
   * aSTRING+ (CONTEXT.md, *Entitlement*), so the demo is showing a paid feature.
   */
  images?: string[];
  messages: {
    /** Pool pseudonym in an anonymous Loop, username in a named one. */
    sender: string;
    /** Pseudonym emoji. Named senders show an initial instead. */
    emoji?: string;
    text: string;
    /** Rendered as the reader's own message — accent bubble, right-aligned. */
    own?: boolean;
  }[];
}

/*
  The pseudonyms are real ones: `POOL` in packages/services/loop-pseudonym.ts,
  which is a cross-platform contract pinned by golden vectors in three
  languages. Inventing prettier ones here would show a product that does not
  exist.
*/
export const LOOP_EXAMPLES: LoopExample[] = [
  {
    id: "finals",
    topic: "Finals week survival",
    anonymous: true,
    author: "Cedar Wren",
    messages: [
      { sender: "Dune Fox", emoji: "🦊", text: "is the library open at 2am during finals or not" },
      { sender: "Cedar Wren", emoji: "🪶", text: "yes. 3rd floor only" },
      { sender: "Cedar Wren", emoji: "🪶", text: "and the heating works up here, unlike downstairs" },
      { sender: "Dune Fox", emoji: "🦊", text: "on my way" },
      { sender: "Sage Owl", emoji: "🦉", text: "does anyone actually understand question 4" },
      { sender: "You", text: "no. absolutely not.", own: true },
    ],
  },
  {
    id: "tickets",
    topic: "Two spare tickets for Friday",
    anonymous: false,
    author: "Enkhjin",
    images: [comedyNight, comedyLineup],
    messages: [
      { sender: "Enkhjin", text: "two spare for friday. first come" },
      { sender: "Tuguldur", text: "me! still free?" },
      { sender: "Enkhjin", text: "yours. see you there" },
      { sender: "Saruul", text: "how much do i owe you for it" },
      { sender: "Enkhjin", text: "nothing. just turn up on time" },
      { sender: "You", text: "if a third one turns up i'm in", own: true },
    ],
  },
  {
    id: "heating",
    topic: "Is anyone else's dorm heating out?",
    anonymous: true,
    author: "Moss Badger",
    archived: true,
    messages: [
      { sender: "Moss Badger", emoji: "🦡", text: "block C has been cold since tuesday" },
      { sender: "Ember Moth", emoji: "🔥", text: "same in B. i've been sleeping in a coat" },
      { sender: "Willow Hare", emoji: "🐇", text: "has anyone actually reported it" },
      { sender: "Moss Badger", emoji: "🦡", text: "twice. no answer either time" },
      { sender: "River Fern", emoji: "🌿", text: "they fixed B this morning. C still nothing" },
    ],
  },
];

/*
  The same three Loops for `mn`, written as a Mongolian campus week rather than
  translated: the -30° morning, the library's warm floor, the dorm heating. The
  structure is the English set's exactly (ids, identity modes, the archived
  room, the header images), which `constants.test.ts`
  pins, so a locale can never add a count or a room the other does not have.
  Pseudonyms stay the pool's own names: they are what the product shows.
*/
export const LOOP_EXAMPLES_MN: LoopExample[] = [
  {
    id: "finals",
    topic: "Шалгалтын долоо хоног",
    anonymous: true,
    author: "Cedar Wren",
    messages: [
      { sender: "Dune Fox", emoji: "🦊", text: "шалгалтын үеэр номын сан шөнийн 2 цагт онгорхой юу, үгүй юу" },
      { sender: "Cedar Wren", emoji: "🪶", text: "онгорхой. зөвхөн 3-р давхар" },
      { sender: "Cedar Wren", emoji: "🪶", text: "дээр халаалт нь ажиллаж байгаа, доод давхраас ялгаатай нь" },
      { sender: "Dune Fox", emoji: "🦊", text: "гарлаа. гадаа −30 байна шүү" },
      { sender: "Sage Owl", emoji: "🦉", text: "4-р бодлогыг үнэхээр ойлгосон хүн байна уу" },
      { sender: "You", text: "үгүй. огт үгүй.", own: true },
    ],
  },
  {
    id: "tickets",
    topic: "Баасангийн хоёр илүү тасалбар",
    anonymous: false,
    author: "Энхжин",
    images: [comedyNight, comedyLineup],
    messages: [
      { sender: "Энхжин", text: "баасан гарагийнх хоёр илүү тасалбар байна. түрүүлж хэлсэн нь авна" },
      { sender: "Төгөлдөр", text: "би! байгаа юу?" },
      { sender: "Энхжин", text: "чинийх. тэнд уулзъя" },
      { sender: "Саруул", text: "би хэдийг шилжүүлэх вэ" },
      { sender: "Энхжин", text: "юу ч үгүй. зүгээр л цагтаа ирээрэй" },
      { sender: "You", text: "гурав дахь нь гарвал би явна", own: true },
    ],
  },
  {
    id: "heating",
    topic: "Байрны халаалт унтарсан уу?",
    anonymous: true,
    author: "Moss Badger",
    archived: true,
    messages: [
      { sender: "Moss Badger", emoji: "🦡", text: "C байр мягмар гарагаас хойш хүйтэн байна" },
      { sender: "Ember Moth", emoji: "🔥", text: "B-д ч гэсэн. пальтотойгоо унтаж байна" },
      { sender: "Willow Hare", emoji: "🐇", text: "жижүүрт хэлсэн хүн байна уу" },
      { sender: "Moss Badger", emoji: "🦡", text: "хоёр удаа. хоёуланд нь хариу алга" },
      { sender: "River Fern", emoji: "🌿", text: "B-гийнхийг өнөө өглөө зассан. C одоо ч хүйтэн" },
    ],
  },
];

/** The demo-content locale for an i18n language tag: `en` for any English tag, else `mn` (the default locale). */
export const demoLocale = (language: string | undefined): "mn" | "en" =>
  language?.split("-")[0] === "en" ? "en" : "mn";

export const loopExamplesFor = (language: string | undefined): LoopExample[] =>
  demoLocale(language) === "en" ? LOOP_EXAMPLES : LOOP_EXAMPLES_MN;
